/**
 * listingSpecs.js — single source of truth for a listing's "Trailer Details" specs.
 *
 * Used by BOTH:
 *   - SectionDetailsMaybe.js (renders the visible iconed spec-card grid), and
 *   - ListingPageCarousel.js (builds JSON-LD Product.additionalProperty).
 *
 * Keeping ONE builder guarantees the visible cards and the structured data stay
 * in perfect sync (SEO hard-rule: what Googlebot reads in the DOM must match the
 * JSON-LD). Every value here is plain text destined for SSR HTML — no icons carry
 * data, nothing is client-only.
 *
 * Two data sources are unified:
 *   1. Config-driven fields (enum / boolean / long) flagged `isDetail` in the
 *      Sharetribe hosted listing-field config (labels come from that config).
 *   2. A fixed set of "additional" fields read straight from publicData
 *      (length, make, model, weight_capacity, width) whose labels come from i18n.
 *
 * Items are de-duped by key (config wins), empty values are dropped, and the
 * result is ordered by DISPLAY_ORDER (renter decision-drivers first).
 */

import { isFieldForListingType } from './fieldHelpers';
import { ENUMS } from './enums';

// Renter-decision-driver order first (Fable), then identity, then hardware.
export const SPEC_DISPLAY_ORDER = [
  'trailer_type',
  'trailerType',
  'length',
  'width',
  'weight_capacity',
  'year',
  'make',
  'model',
  'hitch_type',
  'hitchType',
  'ball_size',
  'ballSize',
  'light_plug_configuration',
  'lightPlugConfiguration',
];

// Keys we deliberately do NOT surface as a detail card (rendered elsewhere).
const EXCLUDED_KEYS = new Set([ENUMS.TRAILER_TYPE]); // 'trailerType'

// i18n label ids for the hardcoded additional (publicData) fields.
const ADDITIONAL_FIELDS = [
  { key: 'length', labelId: 'ListingPage.length' },
  { key: 'make', labelId: 'ListingPage.make' },
  { key: 'model', labelId: 'ListingPage.model' },
  { key: 'weight_capacity', labelId: 'ListingPage.weightCapacity' },
  { key: 'width', labelId: 'ListingPage.width' },
];

const hasText = v => v != null && String(v).trim() !== '';

/**
 * Format a spec value for display. Units mostly already live in the value
 * (e.g. "26 feet", "102 inches"); weight_capacity is a bare number.
 */
export const formatSpecValue = (key, value, intl) => {
  if (!hasText(value)) return null;
  const s = String(value).trim();

  if (key === 'weight_capacity') {
    // Already carries a unit? leave it.
    if (/lb/i.test(s)) return s;
    const n = Number(s.replace(/[^\d.]/g, ''));
    if (Number.isFinite(n) && n > 0) {
      const num = intl ? intl.formatNumber(n, { maximumFractionDigits: 0 }) : n.toLocaleString('en-US');
      return `${num} lbs`;
    }
    // Non-numeric (e.g. "Call for info") — show as-is, no fabricated unit.
    return s;
  }

  return s;
};

/**
 * Build the ordered, de-duped, non-empty list of spec items for a listing.
 *
 * @returns {Array<{key, label, value, iconKey}>}
 */
export const buildSpecItems = ({
  publicData,
  metadata = {},
  listingFieldConfigs = [],
  isFieldForCategory,
  intl,
}) => {
  if (!publicData) return [];

  const listingType = publicData.listingType;
  const byKey = new Map();

  // 1) Config-driven detail fields (enum / boolean / long).
  (listingFieldConfigs || []).forEach(config => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    if (!showConfig.isDetail) return;
    if (!isFieldForListingType(listingType, config)) return;
    if (typeof isFieldForCategory === 'function' && !isFieldForCategory(config)) return;
    if (EXCLUDED_KEYS.has(key)) return;

    // BUGFIX: original code used `typeof pd != null` (always true). Use `!= null`.
    const pd = publicData[key];
    const md = metadata[key];
    const value = pd != null ? pd : md;
    if (!hasText(value)) return;

    let valueText;
    if (schemaType === 'enum') {
      const opt = enumOptions?.find(o => `${o.option}` === `${value}`);
      valueText = opt?.label || String(value);
    } else if (schemaType === 'boolean') {
      valueText = value
        ? (intl ? intl.formatMessage({ id: 'SearchPage.detailYes' }) : 'Yes')
        : (intl ? intl.formatMessage({ id: 'SearchPage.detailNo' }) : 'No');
    } else if (schemaType === 'long') {
      valueText = String(value);
    } else {
      return; // text/multi-enum/etc. handled elsewhere
    }

    if (!hasText(valueText)) return;
    byKey.set(key, { key, label: showConfig.label || key, value: valueText, iconKey: key });
  });

  // 2) Hardcoded additional fields from publicData (config wins on dup).
  ADDITIONAL_FIELDS.forEach(({ key, labelId }) => {
    if (byKey.has(key)) return;
    const raw = publicData[key];
    if (!hasText(raw)) return;
    const value = formatSpecValue(key, raw, intl);
    if (!hasText(value)) return;
    const label = intl ? intl.formatMessage({ id: labelId }) : key;
    byKey.set(key, { key, label, value, iconKey: key });
  });

  // 3) Order by DISPLAY_ORDER, unknown keys sink to the end (stable by key).
  const orderIndex = k => {
    const i = SPEC_DISPLAY_ORDER.indexOf(k);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return Array.from(byKey.values()).sort((a, b) => {
    const ai = orderIndex(a.key);
    const bi = orderIndex(b.key);
    return ai !== bi ? ai - bi : a.key.localeCompare(b.key);
  });
};

// ===========================================================================
// "The Rundown" — auto-generated, unique, crawlable SEO paragraph per listing.
//
// WHY: ~63% of listings have thin owner descriptions (<160 chars). This ADDS a
// second block of unique, keyword-rich, geo-targeted prose built ENTIRELY from
// structured data we already own (never rewrites the owner's description).
//
// ANTI-SPUN-CONTENT DISCIPLINE (this is what keeps it on the right side of
// Google's doorway/templated-content filters):
//   1. Sentence STRUCTURE varies per listing — 4 rotating template sets picked
//      deterministically by a hash of the listing id (fnv1a). So the *shape* of
//      the paragraph differs across listings, not merely the {year make model}.
//   2. We ONLY assert facts we actually have. Every clause degrades gracefully
//      when its data is missing or junk. NOTHING is fabricated.
//   3. Messy real-world data is normalized (weight "14k" -> "14,000 lbs",
//      "7000" -> "7,000 lbs") and non-dimensional junk (e.g. a width value of
//      "Any full size truck ...") is detected and dropped.
//
// SSR-ONLY: the output is plain text rendered into a <p> server-side, so
// Googlebot always reads it in the DOM. Nothing here is client-only.
// ===========================================================================

// Canonical US state name -> URL slug (matches the CMS `trailer-rentals-<slug>`
// state-hub slugs, verified live). Used to safely internal-link the state name
// in the paragraph to its state hub. Cities are intentionally NOT linked: their
// hub slugs are CMS-arbitrary and a missing one returns a soft-404 (HTTP 200),
// so linking them would risk broken internal links = an SEO regression.
const US_STATE_SLUGS = {
  alabama: 'alabama', alaska: 'alaska', arizona: 'arizona', arkansas: 'arkansas',
  california: 'california', colorado: 'colorado', connecticut: 'connecticut',
  delaware: 'delaware', florida: 'florida', georgia: 'georgia', hawaii: 'hawaii',
  idaho: 'idaho', illinois: 'illinois', indiana: 'indiana', iowa: 'iowa',
  kansas: 'kansas', kentucky: 'kentucky', louisiana: 'louisiana', maine: 'maine',
  maryland: 'maryland', massachusetts: 'massachusetts', michigan: 'michigan',
  minnesota: 'minnesota', mississippi: 'mississippi', missouri: 'missouri',
  montana: 'montana', nebraska: 'nebraska', nevada: 'nevada',
  'new hampshire': 'new-hampshire', 'new jersey': 'new-jersey',
  'new mexico': 'new-mexico', 'new york': 'new-york',
  'north carolina': 'north-carolina', 'north dakota': 'north-dakota',
  ohio: 'ohio', oklahoma: 'oklahoma', oregon: 'oregon', pennsylvania: 'pennsylvania',
  'rhode island': 'rhode-island', 'south carolina': 'south-carolina',
  'south dakota': 'south-dakota', tennessee: 'tennessee', texas: 'texas',
  utah: 'utah', vermont: 'vermont', virginia: 'virginia', washington: 'washington',
  'west virginia': 'west-virginia', wisconsin: 'wisconsin', wyoming: 'wyoming',
  'district of columbia': 'district-of-columbia',
};

// Two-letter abbreviations -> full name, so an abbreviated `state` still links.
const US_STATE_ABBREV = {
  al: 'alabama', ak: 'alaska', az: 'arizona', ar: 'arkansas', ca: 'california',
  co: 'colorado', ct: 'connecticut', de: 'delaware', fl: 'florida', ga: 'georgia',
  hi: 'hawaii', id: 'idaho', il: 'illinois', in: 'indiana', ia: 'iowa',
  ks: 'kansas', ky: 'kentucky', la: 'louisiana', me: 'maine', md: 'maryland',
  ma: 'massachusetts', mi: 'michigan', mn: 'minnesota', ms: 'mississippi',
  mo: 'missouri', mt: 'montana', ne: 'nebraska', nv: 'nevada', nh: 'new hampshire',
  nj: 'new jersey', nm: 'new mexico', ny: 'new york', nc: 'north carolina',
  nd: 'north dakota', oh: 'ohio', ok: 'oklahoma', or: 'oregon', pa: 'pennsylvania',
  ri: 'rhode island', sc: 'south carolina', sd: 'south dakota', tn: 'tennessee',
  tx: 'texas', ut: 'utah', vt: 'vermont', va: 'virginia', wa: 'washington',
  wv: 'west virginia', wi: 'wisconsin', wy: 'wyoming', dc: 'district of columbia',
};

// Trailer-type slug -> { label, uses }. `uses` feeds a natural "ideal for..."
// closing clause. Keys match the raw publicData enum slugs.
const TRAILER_TYPE_INFO = {
  'utility-trailer': { label: 'utility trailer', uses: 'moving, landscaping, and general hauling' },
  'car-hauler': { label: 'car hauler', uses: 'transporting cars, trucks, and other vehicles' },
  'dump-trailer': { label: 'dump trailer', uses: 'debris removal, dirt, gravel, and demolition cleanup' },
  'enclosed-trailer': { label: 'enclosed trailer', uses: 'moving valuables, dry cargo, and weather-sensitive loads' },
  'cargo-trailer': { label: 'cargo trailer', uses: 'moving, deliveries, and protected cargo transport' },
  'flatbed-trailer': { label: 'flatbed trailer', uses: 'hauling equipment, materials, and oversized loads' },
  'equipment-trailer': { label: 'equipment trailer', uses: 'moving tractors, skid steers, and heavy equipment' },
  'deckover-trailer': { label: 'deckover trailer', uses: 'wide equipment and oversized flat loads' },
  'gooseneck-trailer': { label: 'gooseneck trailer', uses: 'heavy hauling and large equipment transport' },
  'motorcycle-trailer': { label: 'motorcycle trailer', uses: 'hauling motorcycles and small powersports' },
  'atv-snowmobile': { label: 'ATV & snowmobile trailer', uses: 'hauling ATVs, UTVs, and snowmobiles' },
  'toy-hauler': { label: 'toy hauler', uses: 'hauling ATVs, dirt bikes, and recreational gear' },
  'boat-trailer': { label: 'boat trailer', uses: 'launching and transporting boats' },
  'livestock-trailer': { label: 'livestock trailer', uses: 'transporting cattle and livestock' },
  'horse-trailer': { label: 'horse trailer', uses: 'transporting horses safely' },
  'tow-dolly': { label: 'tow dolly', uses: 'flat-towing a front-wheel-drive vehicle' },
  'fifth-wheel': { label: 'fifth-wheel trailer', uses: 'heavy towing and large hauls' },
};

// fnv1a-1a 32-bit — same deterministic hash family used by the home-feed day-pick
// work, so template selection is stable per listing (no server/client drift, no
// churn on re-render).
const fnv1a = str => {
  let h = 0x811c9dc5;
  const s = String(str || '');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
};

const clean = v => (v == null ? '' : String(v).trim());

// A dimension value is USABLE only if it starts with a number. This drops the
// free-text junk found in the data ("Any full size truck ...", "Interior Width:
// 68", "Total w") that would otherwise produce nonsense sentences.
//
// `kind` ('length' | 'width') sets a plausible range so obviously-wrong values
// are skipped rather than printed (e.g. a width of "77 ft" is a data error).
const normalizeDimension = (raw, kind) => {
  const s = clean(raw);
  if (!s) return null;
  if (!/^\d/.test(s)) return null; // must LEAD with a digit
  const n = parseFloat(s.replace(/[^\d.].*$/, ''));
  if (!Number.isFinite(n) || n <= 0) return null;
  const isInches = /in(ch(es)?)?\b|"|''/i.test(s);
  if (isInches) {
    // Inches: width 24-140", length up to ~600". Reject out-of-range junk.
    const maxIn = kind === 'width' ? 140 : 600;
    if (n < 12 || n > maxIn) return null;
    return `${n} in`;
  }
  // Feet (bare number or explicit ft). Width in feet realistically <= 14,
  // length <= 60. Anything larger is a mis-entered value -> skip.
  const maxFt = kind === 'width' ? 14 : 60;
  if (n > maxFt) return null;
  return `${n} ft`;
};

// Weight -> "N,NNN lbs". Handles "14k", "7000", "10,000", "1600 pounds",
// "6,500lbs", ranges ("42,000 to 45,000 pounds" -> uses the first number).
// Returns null for non-numeric junk ("Call for info").
const normalizeWeight = raw => {
  const s = clean(raw).toLowerCase();
  if (!s) return null;
  // "14k" / "14 k" -> 14000 (only when 'k' directly follows the number).
  const kMatch = s.match(/(\d[\d.,]*)\s*k\b/);
  let n;
  if (kMatch) {
    n = parseFloat(kMatch[1].replace(/,/g, '')) * 1000;
  } else {
    const m = s.match(/\d[\d,]*(\.\d+)?/);
    if (!m) return null;
    n = parseFloat(m[0].replace(/,/g, ''));
  }
  if (!Number.isFinite(n) || n <= 0 || n > 200000) return null;
  return `${Math.round(n).toLocaleString('en-US')} lbs`;
};

const titleCaseState = name =>
  clean(name)
    .split(/\s+/)
    .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');

// Resolve a listing's `state` free-text into { name, slug } if it's a real US
// state (full name or 2-letter abbrev). Non-US / unknown -> null (won't link).
export const resolveStateHub = rawState => {
  const key = clean(rawState).toLowerCase();
  if (!key) return null;
  // Resolve to a canonical full state name: direct hit, abbreviation, or an
  // already-hyphenated form ("new-york" -> "new york").
  let full = null;
  if (US_STATE_SLUGS[key]) full = key;
  else if (US_STATE_ABBREV[key]) full = US_STATE_ABBREV[key];
  else {
    const spaced = key.replace(/-/g, ' ');
    if (US_STATE_SLUGS[spaced]) full = spaced;
  }
  if (!full || !US_STATE_SLUGS[full]) return null;
  return { name: titleCaseState(full), slug: `trailer-rentals-${US_STATE_SLUGS[full]}` };
};

// Parse the multi-value `trailerType` (array OR comma string) into resolved
// primary info. Multi-type listings list several "what it can haul" tags; the
// FIRST tag is often not the trailer's actual type. So when the listing title
// mentions one of the tagged types, prefer THAT as the primary (more accurate).
// Returns { primaryLabel, uses, allLabels } or null.
const resolveTrailerTypes = (raw, title) => {
  let list = [];
  if (Array.isArray(raw)) list = raw;
  else if (typeof raw === 'string' && raw.trim()) list = raw.split(',');
  list = list.map(clean).filter(Boolean);
  if (list.length === 0) return null;
  const known = list.filter(t => TRAILER_TYPE_INFO[t]);
  if (known.length === 0) return { primaryLabel: null, uses: null, allLabels: [] };
  // Title-aware primary selection.
  const t = clean(title).toLowerCase();
  let primary = known[0];
  if (t) {
    const titleMatch = known.find(k => {
      const core = TRAILER_TYPE_INFO[k].label.replace(/\s*trailer$/i, '').trim().toLowerCase();
      return core && t.includes(core);
    });
    if (titleMatch) primary = titleMatch;
  }
  const info = TRAILER_TYPE_INFO[primary];
  return {
    primaryLabel: info ? info.label : null,
    uses: info ? info.uses : null,
    allLabels: known.map(k => TRAILER_TYPE_INFO[k].label),
  };
};

/**
 * Build "The Rundown" — a unique 2-4 sentence SEO paragraph for a listing.
 *
 * @param {Object} args
 * @param {Object} args.publicData    listing.attributes.publicData
 * @param {String} [args.priceText]   pre-formatted price (e.g. "$60"); optional
 * @param {String} [args.priceUnit]   unit label (e.g. "day"); optional
 * @param {String} args.listingId     listing.id.uuid (deterministic template pick)
 * @returns {{ segments: Array, stateHub: ?{name,slug}, plainText: string } | null}
 *          `segments` is an array of strings and {stateName, stateSlug} link
 *          tokens for rich rendering; `plainText` is the same content flattened
 *          (used for JSON-LD / meta). Returns null when there isn't enough data.
 */
export const buildRundownText = ({
  publicData,
  title,
  priceText,
  priceUnit = 'day',
  listingId,
} = {}) => {
  if (!publicData) return null;

  const year = clean(publicData.year);
  const make = clean(publicData.make);
  const model = clean(publicData.model);
  const city = clean(publicData.city);
  const stateRaw = clean(publicData.state);
  const length = normalizeDimension(publicData.length, 'length');
  const width = normalizeDimension(publicData.width, 'width');
  const weight = normalizeWeight(publicData.weight_capacity);
  const hitch = clean(publicData.hitch_type);
  const ball = clean(publicData.ball_size);
  const tt = resolveTrailerTypes(publicData.trailerType, title);
  const stateHub = resolveStateHub(stateRaw);

  // Identity summary: "2024 J&E 6x10 utility trailer" style. Prefer YMM; fall
  // back to type label; if we truly have nothing to say, bail (no empty block).
  const ymm = [year, make, model].filter(Boolean).join(' ');
  const typeWord = (tt && tt.primaryLabel) || 'trailer';
  // Avoid doubling the type word when make/model already contains it
  // (e.g. model "6x10 Utility Trailer" + type "utility trailer").
  const typeLabel = tt && tt.primaryLabel ? tt.primaryLabel : 'trailer';
  const ymmLower = ymm.toLowerCase();
  const typeCore = typeLabel.replace(/\s*trailer$/i, '').trim().toLowerCase();
  const alreadyHasType =
    ymmLower.includes(typeLabel.toLowerCase()) ||
    (typeCore && ymmLower.includes(typeCore) && /trailer|hauler|dolly/i.test(ymmLower));
  const subject = ymm ? (alreadyHasType ? ymm : `${ymm} ${typeLabel}`) : typeWord;

  // Need at least an identity + one concrete fact, otherwise the paragraph adds
  // no unique value over the spec cards.
  const facts = [length, width, weight, tt && tt.primaryLabel].filter(Boolean).length;
  if (!ymm && !city && facts < 2) return null;

  // Location fragment (as segment tokens so the state can be a link).
  // "in {city}, {StateLink}" / "in {StateLink}" / "in {city}" / ""
  const locationSegments = [];
  const hasCity = !!city;
  if (hasCity || stateHub || stateRaw) {
    locationSegments.push('in ');
    if (hasCity) locationSegments.push(city);
    if (hasCity && (stateHub || stateRaw)) locationSegments.push(', ');
    if (stateHub) locationSegments.push({ stateName: stateHub.name, stateSlug: stateHub.slug });
    else if (stateRaw) locationSegments.push(titleCaseState(stateRaw));
  }
  // Clean the display price: "$60.00" -> "$60" (drop a whole-dollar .00 tail).
  const priceClean = clean(priceText).replace(/(\$\d[\d,]*)\.00\b/, '$1');
  const priceFrag = priceClean ? `starting at ${priceClean} per ${priceUnit}` : null;

  // Dimensions clause. "measures 10 ft long by 6 ft wide" (either alone is OK).
  let dimClause = null;
  if (length && width) dimClause = `measures ${length} long by ${width} wide`;
  else if (length) dimClause = `is ${length} long`;
  else if (width) dimClause = `is ${width} wide`;

  const capacityClause = weight ? `carries up to ${weight}` : null;

  // Hitch clause — only when the hitch value is a clean, human phrase. Enum
  // slugs ("bumper_pull", "2_5_16_inch_ball") are de-slugged; obvious junk
  // (single chars, stray punctuation) is skipped.
  const deslug = v =>
    clean(v)
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  // Ball-size enum slugs use underscores for the fraction: "2_5_16_inch_ball"
  // = a 2-5/16" ball; "1_7_8_inch_ball" = 1-7/8". De-slug those specially.
  const formatBall = v => {
    const s = clean(v);
    if (!s) return null;
    const m = s.match(/^(\d)_(\d+)_(\d+)_inch_ball$/i); // whole_num_den
    if (m) return `${m[1]}-${m[2]}/${m[3]} inch ball`;
    const m2 = s.match(/^(\d+)_inch_ball$/i);
    if (m2) return `${m2[1]} inch ball`;
    if (/^ball_size_other$/i.test(s)) return null; // no useful info
    if (!/\d/.test(s)) return null; // junk without a size number
    return deslug(s); // free-text like '2 5/16"' passes through cleaned
  };
  const hitchWord = hitch && /[a-z]{3,}/i.test(hitch) ? deslug(hitch).toLowerCase() : null;
  const ballWord = formatBall(ball);
  let hitchClause = null;
  if (hitchWord && ballWord) hitchClause = `uses a ${hitchWord} hitch with a ${ballWord}`;
  else if (hitchWord) hitchClause = `uses a ${hitchWord} hitch`;

  const usesClause = tt && tt.uses ? `Ideal for ${tt.uses}` : null;

  // ---- Deterministic template selection (varies STRUCTURE per listing) ----
  const templateCount = 4;
  // Deterministic per-listing template pick. listingId (the uuid) is always
  // present in production; the fallback folds in as much distinguishing data
  // as we have so minimal-data listings without an id still spread across
  // templates rather than all collapsing to one.
  const hashSeed =
    clean(listingId) ||
    `${subject}|${city}|${stateRaw}|${make}|${model}|${length}|${width}|${weight}`;
  const pick = fnv1a(hashSeed) % templateCount;

  // Each template composes the clauses in a DIFFERENT order/voice. Missing
  // clauses drop out; we then stitch surviving sentences with a leading capital.
  const cap = str => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str);

  // Sentence 1 builders return an array of segments (to preserve the state link).
  const s1Variants = [
    // 0: "This {subject} is available for rent {loc}, {price}."
    () => {
      const seg = [`This ${subject} is available for rent `];
      if (locationSegments.length) seg.push(...locationSegments);
      if (priceFrag) seg.push(`${locationSegments.length ? ', ' : ''}${priceFrag}`);
      seg.push('.');
      return seg;
    },
    // 1: "Rent this {subject} {loc}{ — price}."
    () => {
      const seg = [`Rent this ${subject} `];
      if (locationSegments.length) seg.push(...locationSegments);
      if (priceFrag) seg.push(`${locationSegments.length ? ' — ' : ''}${priceFrag}`);
      seg.push('.');
      return seg;
    },
    // 2: "Looking for a {typeWord} {loc}? This {subject} is {price}."
    () => {
      const seg = [];
      if (locationSegments.length) {
        seg.push(`Looking for a ${typeWord} `);
        seg.push(...locationSegments);
        seg.push(`? This ${subject} is available for rent`);
        if (priceFrag) seg.push(` ${priceFrag}`);
        seg.push('.');
      } else {
        seg.push(`This ${subject} is available for rent`);
        if (priceFrag) seg.push(` ${priceFrag}`);
        seg.push('.');
      }
      return seg;
    },
    // 3: "{price-caps}, this {subject} is ready to rent {loc}." — distinct
    // opening voice (price-led) so it doesn't mirror templates 0/1/2.
    () => {
      const seg = [];
      if (priceFrag) {
        seg.push(`Available ${priceFrag}, this ${subject} is ready to rent `);
      } else {
        seg.push(`This ${subject} is ready to rent `);
      }
      if (locationSegments.length) seg.push(...locationSegments);
      else seg.push('now');
      seg.push('.');
      return seg;
    },
  ];

  // Sentence 2: specs. Compose the surviving spec clauses in a template-specific
  // order/connector so identical-spec listings still read differently.
  const specClauses = [dimClause, capacityClause, hitchClause].filter(Boolean);
  const buildS2 = () => {
    if (specClauses.length === 0) return '';
    const subjPronoun = ['It ', 'This trailer ', 'It ', 'The trailer '][pick];
    // join: "a, b and c"
    const joined =
      specClauses.length === 1
        ? specClauses[0]
        : specClauses.slice(0, -1).join(', ') + ' and ' + specClauses[specClauses.length - 1];
    return `${subjPronoun}${joined}.`;
  };

  // Assemble segments: sentence1 (segments) + " " + sentence2 + " " + uses.
  const segments = [];
  const s1 = s1Variants[pick]();
  // Ensure sentence starts capitalized (first token is a string here).
  if (typeof s1[0] === 'string') s1[0] = cap(s1[0]);
  segments.push(...s1);

  const s2 = buildS2();
  if (s2) segments.push(` ${s2}`);

  // Uses clause closes the paragraph. Location is intentionally NOT repeated
  // here — it's already established in sentence 1 (avoids robotic "...in
  // Austin, Texas. ... in Austin, Texas.").
  if (usesClause) segments.push(` ${usesClause}.`);

  // Flatten to plainText (state link -> its name) for JSON-LD / meta reuse.
  const plainText = segments
    .map(s => (typeof s === 'string' ? s : s.stateName))
    .join('')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,?])/g, '$1')
    .trim();

  if (!plainText || plainText.length < 40) return null;

  return { segments, stateHub, plainText };
};
